import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

/**
 * Bulk 4×6 labels for every box generated by an assembly run.
 * Each label is a full page (page-break-after) so a roll printer pumps out one per box.
 */
export default function AssemblyRunLabels() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [settings, setSettings] = useState({ company_name: "US Operations" });

  useEffect(() => {
    (async () => {
      const [r, allBoxes, s] = await Promise.all([
        api.get(`/assembly-runs/${id}`), api.get("/boxes"), api.get("/settings"),
      ]);
      setRun(r.data);
      setBoxes(allBoxes.data.filter((b) => (r.data.box_ids || []).includes(b.id)));
      setSettings(s.data);
    })();
  }, [id]);

  if (!run) return <div className="font-display uppercase">Loading...</div>;

  if (boxes.length === 0) {
    return (
      <div className="p-8" data-testid="run-labels-empty">
        <div className="brut-card p-6">
          <h2 className="font-display text-2xl uppercase">No boxes for this run</h2>
          <p className="text-sm mt-2 text-slate-600">Complete the run first to auto-create boxes.</p>
          <Link to={`/assembly-runs/${id}`} className="brut-btn px-3 py-2 mt-3 inline-block">Back to Run</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="run-labels-page">
      <style>{`
        @page { size: 4in 6in; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .label-wrap { margin: 0 !important; padding: 0 !important; }
          .label-4x6 { page-break-after: always; box-shadow: none !important; border-width: 0 !important; }
          .no-print { display: none !important; }
          .toolbar { display: none !important; }
        }
        .label-4x6 {
          width: 4in; height: 6in;
          padding: 0.25in;
          background: #fff;
          color: #1e293b;
          font-family: 'IBM Plex Mono', monospace;
          box-sizing: border-box;
          border: 2px solid #334155;
          display: flex; flex-direction: column;
          margin: 12px auto;
        }
      `}</style>

      <div className="flex items-center justify-between p-4 toolbar">
        <Link to={`/assembly-runs/${id}`} className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back to Run
        </Link>
        <div className="font-mono-ui text-xs">
          {boxes.length} label{boxes.length === 1 ? "" : "s"} · Run {run.grn_number}
        </div>
        <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="print-all-labels-btn">
          <Printer size={14} weight="bold" /> Print All Labels
        </button>
      </div>

      <div className="label-wrap py-4">
        {boxes.map((box, idx) => {
          const traceUrl = `${window.location.origin}/trace?box=${box.box_number}`;
          return (
            <div key={box.id} className="label-4x6" data-testid={`label-${box.box_number}`}>
              <div style={{ borderBottom: "2px solid #334155", paddingBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <img src="/logo-gate.jpg" alt="GATE" style={{ width: 36, height: 36, border: "1.5px solid #334155", objectFit: "cover" }} />
                <div>
                  <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11, letterSpacing: 2, color: "#1d4ed8" }}>// {settings.company_name?.toUpperCase()}</div>
                  <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 20, lineHeight: 1, marginTop: 2 }}>SHIPPING LABEL</div>
                </div>
                <div style={{ marginLeft: "auto", fontSize: 10, color: "#64748b" }}>{idx + 1} / {boxes.length}</div>
              </div>

              <div style={{ marginTop: 10, textAlign: "center" }}>
                <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#64748b" }}>Box Number</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 32, letterSpacing: -1, lineHeight: 1 }}>{box.box_number}</div>
              </div>

              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
                <div style={{ border: "1.5px solid #334155", padding: 6 }}>
                  <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: "#64748b" }}>Product</div>
                  <div style={{ fontWeight: 800 }}>{box.product_part_number || "—"}</div>
                  <div style={{ fontSize: 9, lineHeight: 1.2 }}>{box.product_name}</div>
                </div>
                <div style={{ border: "1.5px solid #334155", padding: 6 }}>
                  <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: "#64748b" }}>Units</div>
                  <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 26, lineHeight: 1 }}>{box.units_count}</div>
                </div>
              </div>

              <div style={{ marginTop: 8, fontSize: 10 }}>
                <div><b>Project:</b> {box.target_project || "—"}</div>
                <div><b>Assembly Run:</b> {box.assembly_run_number || "—"}</div>
                <div><b>Assembled:</b> {new Date(box.created_at).toLocaleDateString()}</div>
              </div>

              <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8, borderTop: "2px solid #334155" }}>
                <QRCodeSVG value={traceUrl} size={150} level="M" bgColor="#fff" fgColor="#1e293b" />
                <div style={{ fontSize: 9, letterSpacing: 1, marginTop: 4 }}>Scan for traceability</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 12, marginTop: 2 }}>{box.box_number}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
